import { useState } from 'react';
import { Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { ComposeMessageDialog } from '@/components/compose';
import { cn } from '@/lib/utils';
import { useLocalizations } from '@/hooks/useLocalizations';

interface MessageButtonProps {
  userId: string;
  userName: string;
  userAvatar?: string;
  userLocationId?: number;
  variant?: 'default' | 'ghost' | 'outline';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
}

export function MessageButton({ 
  userId, 
  userName,
  userAvatar,
  userLocationId,
  variant = 'ghost', 
  size = 'icon',
  className
}: MessageButtonProps) {
  const { lComponents } = useLocalizations();
  const [open, setOpen] = useState(false);

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={variant}
              size={size}
              className={cn(className)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Small delay to let tooltip dismiss before dialog captures focus
                setTimeout(() => setOpen(true), 10);
              }}
            >
              <Mail className={cn(
                'h-4 w-4 transition-all',
                'text-muted-foreground hover:text-primary'
              )} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {lComponents('message_button.tooltip', { defaultValue: 'Send a message' })}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {open && (
        <ComposeMessageDialog
          open={open}
          onOpenChange={setOpen}
          initialRecipients={[{
            id: userId,
            name: userName,
            image: userAvatar
          }]}
          lockedRecipients={true}
          context="frontend"
          hideTrigger
        />
      )}
    </>
  );
}
